import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import classnames from 'classnames';

import { getTrialIcon } from '#entities';
import { ENOPOrganType, EResourceStatus, EResourceType } from '#shared';

import { CellDrawerCounts } from './CellDrawerCounts';
import { cellDrawerClasses } from './CellDrawerRoot';

import type { HumanResource } from '#entities';

type Props = {
  day: 'today' | 'tomorrow';
  resources: HumanResource[];
  title: string;
  type: EResourceType;
  selectedOrgan: ENOPOrganType;
};

export const CellHumanDrawerContent = ({
  day,
  resources,
  type,
  selectedOrgan,
}: Props) => {
  const available = resources.filter(
    r => r[day]?.status === EResourceStatus.Available,
  );
  const assigned = resources.filter(
    r => r[day]?.status === EResourceStatus.Assigned,
  );
  const returning = resources
    .filter(r => r[day]?.status === EResourceStatus.OnRest)
    .sort((a, b) =>
      (a[day]?.endUTCDateTime ?? '') < (b[day]?.endUTCDateTime ?? '') ? -1 : 1,
    );

  const shown = [...assigned, ...available];

  return (
    <>
      <Box className={cellDrawerClasses.resourceTableContainer}>
        <CellDrawerCounts
          assignedCount={assigned.length}
          availableCount={available.length}
        />
        {shown.length > 0 && (
          <Table className={cellDrawerClasses.humanResources}>
            <TableHead>
              <TableRow>
                <TableCell className={cellDrawerClasses.resourceName}>
                  User
                </TableCell>
                <TableCell
                  className={cellDrawerClasses.resourceScheduleSubType}
                >
                  Schedule Sub Type
                </TableCell>
                <TableCell className={cellDrawerClasses.resourceDateTime}>
                  Start
                </TableCell>
                <TableCell className={cellDrawerClasses.resourceDateTime}>
                  End
                </TableCell>
                <TableCell className={cellDrawerClasses.resourceAssignedTo}>
                  Assigned to
                </TableCell>
                {type === EResourceType.Specialist && (
                  <TableCell className={cellDrawerClasses.resourceTrial}>
                    Trial Cert
                  </TableCell>
                )}
                <TableCell className={cellDrawerClasses.resourceStatus}>
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((resource, index) => (
                <TableRow
                  key={resource.name}
                  className={classnames((index % 2 && 'even') || 'odd', {
                    [cellDrawerClasses.resourceOnRest]:
                      day === 'tomorrow' &&
                      resource.today?.status === EResourceStatus.OnRest,
                  })}
                >
                  <TableCell className={cellDrawerClasses.resourceName}>
                    <Box className="inner">{resource.name}</Box>
                  </TableCell>
                  <TableCell
                    className={cellDrawerClasses.resourceScheduleSubType}
                  >
                    {resource[day]?.scheduleType}
                  </TableCell>
                  <TableCell className={cellDrawerClasses.resourceDateTime}>
                    <Box className={cellDrawerClasses.resourceDate}>
                      {resource[day]?.startDate}
                    </Box>
                    <Box className={cellDrawerClasses.resourceTime}>
                      {resource[day]?.startTime}
                    </Box>
                  </TableCell>
                  <TableCell className={cellDrawerClasses.resourceDateTime}>
                    <Box className={cellDrawerClasses.resourceDate}>
                      {resource[day]?.endDate}
                    </Box>
                    <Box className={cellDrawerClasses.resourceTime}>
                      {resource[day]?.endTime}
                    </Box>
                  </TableCell>
                  <TableCell
                    className={classnames(
                      cellDrawerClasses.resourceAssignedTo,
                      cellDrawerClasses.resourceAssignedTo,
                      `organ-type-${selectedOrgan}`,
                    )}
                  >
                    {resource.unosId}
                  </TableCell>

                  {type === EResourceType.Specialist && (
                    <TableCell className={cellDrawerClasses.resourceTrial}>
                      <Box display="flex" gap={1}>
                        {resource.trialPrograms.map(trial => {
                          const TrialIcon = getTrialIcon(trial.name);

                          if (trial.organ !== selectedOrgan) {
                            return null;
                          }

                          return <TrialIcon key={trial.id} />;
                        })}
                      </Box>
                    </TableCell>
                  )}

                  <TableCell className={cellDrawerClasses.resourceStatus}>
                    <span
                      className={classnames(cellDrawerClasses.resourceStatus, {
                        [cellDrawerClasses.assigned]:
                          (resource[day]?.status ?? resource.status) ===
                          EResourceStatus.Assigned,
                        [cellDrawerClasses.available]:
                          (resource[day]?.status ?? resource.status) ===
                          EResourceStatus.Available,
                      })}
                    >
                      {resource[day]?.status ?? resource.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      {returning.length > 0 && (
        <Box className={cellDrawerClasses.resourceReturningTable}>
          <Box className={cellDrawerClasses.headerPrimaryTitle}>
            Returning Resources
          </Box>
          <Table className={cellDrawerClasses.humanResources}>
            <TableHead>
              <TableRow>
                <TableCell className={cellDrawerClasses.resourceName}>
                  User
                </TableCell>
                <TableCell
                  className={cellDrawerClasses.resourceScheduleSubType}
                >
                  Schedule Sub Type
                </TableCell>
                <TableCell className={cellDrawerClasses.resourceAssignedTo}>
                  Assigned to
                </TableCell>
                <TableCell className={cellDrawerClasses.resourceStatus}>
                  Status
                </TableCell>
                <TableCell className={cellDrawerClasses.resourceReturnTime}>
                  Returning
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {returning.map((resource, index) => (
                <TableRow
                  key={resource.name}
                  className={(index % 2 && 'even') || 'odd'}
                >
                  <TableCell className={cellDrawerClasses.resourceName}>
                    {resource.name}
                  </TableCell>
                  <TableCell
                    className={cellDrawerClasses.resourceScheduleSubType}
                  >
                    {resource[day]?.scheduleType}
                  </TableCell>
                  <TableCell
                    className={classnames(
                      cellDrawerClasses.resourceAssignedTo,
                      `organ-type-${selectedOrgan}`,
                    )}
                  >
                    {resource.unosId}
                  </TableCell>
                  <TableCell className={cellDrawerClasses.resourceStatus}>
                    <span>On Rest</span>
                  </TableCell>
                  <TableCell className={cellDrawerClasses.resourceReturnTime}>
                    {resource[day]?.endTime}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </>
  );
};
